import React from 'react';
import { SEGMENT_TRACK, segmentClass } from './tokens';

export interface Segment<T extends string> {
  value: T;
  label: React.ReactNode;
  /** Shown after the label, e.g. how many rows the option holds. */
  count?: number;
}

export interface SegmentedControlProps<T extends string> {
  segments: Segment<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Names the group for screen readers: "Status", "View". */
  'aria-label': string;
}

/**
 * Pick one of a few: a status filter, a view switch. There were seven tab and filter styles.
 *
 * The chosen option is the brand blue with white text (UI-11), so it stands out on every tab bar.
 *
 * These are pressed toggle buttons in a group, not ARIA tabs. Most of them filter a list rather than
 * swap a panel, and page tests already find them as buttons by name.
 */
function SegmentedControl<T extends string>({
  segments,
  value,
  onChange,
  'aria-label': ariaLabel,
}: SegmentedControlProps<T>) {
  return (
    <div role="group" aria-label={ariaLabel} className={SEGMENT_TRACK}>
      {segments.map((segment) => {
        const active = segment.value === value;
        return (
          <button
            key={segment.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(segment.value)}
            className={segmentClass(active)}
          >
            {segment.label}
            {typeof segment.count === 'number' ? (
              <span className={`tabular-nums ${active ? 'text-white/90' : 'text-slate-500'}`}>{segment.count.toLocaleString('en-US')}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export default SegmentedControl;
